import { useContext, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useDebouncedValue } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";

import type { MentalHealthCareProvider } from "@thalamos/aspire-common";

import { CreateEditMentalHealthCareProviderModal } from "./CreateEditMentalHealthCareProviderModal.js";
import { DeleteMentalHealthCareProviderModal } from "./DeleteMentalHealthCareProviderModal.js";
import { api, apiHooks } from "~/api.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  MenuFlyout,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { ManagedLocationsContext } from "~/Contexts.js";

const pageSize = 10;

// Long enough to coalesce a burst of typing into one request, short enough that
// the results still feel like they are keeping up with the input.
const searchDebounceMs = 300;

export interface MentalHealthCareProvidersPageInnerProps {
  providers: MentalHealthCareProvider[];
  trustNamesById: Record<string, string>;
  loading: boolean;
  error: Error | null;
  filterQuery: string;
  onFilterChange: (query: string) => void;
  page: number;
  numPages: number;
  onPageChange: (page: number) => void;
  onAddClick: () => void;
  onEditClick: (provider: MentalHealthCareProvider) => void;
  onDeleteClick: (provider: MentalHealthCareProvider) => void;
}

function MentalHealthCareProviderRow({
  provider,
  trustName,
  onEditClick,
  onDeleteClick,
}: Pick<
  MentalHealthCareProvidersPageInnerProps,
  "onEditClick" | "onDeleteClick"
> & {
  provider: MentalHealthCareProvider;
  trustName: string | undefined;
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const options: MenuOptionsType[] = [
    {
      name: t("pages.mentalHealthCareProviderManagement.table.edit"),
      onClick: () => {
        onEditClick(provider);
      },
      icon: "edit",
      disabled: false,
    },
    {
      name: t("pages.mentalHealthCareProviderManagement.table.delete"),
      onClick: () => {
        onDeleteClick(provider);
      },
      icon: "delete",
      disabled: false,
    },
  ];

  return (
    <TableRow>
      <TableCell>{provider.name}</TableCell>
      <TableCell>{provider.email}</TableCell>
      <TableCell>{trustName ?? "—"}</TableCell>
      <TableCell>
        <Box
          ref={cardRef}
          onClick={() => {
            setIsMenuOpen(true);
          }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function MentalHealthCareProvidersPageInner({
  providers,
  trustNamesById,
  loading,
  error,
  filterQuery,
  onFilterChange,
  page,
  numPages,
  onPageChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
}: MentalHealthCareProvidersPageInnerProps) {
  const { t } = useTranslation();

  // Only the very first fetch shows a spinner instead of the table. Gating the
  // table on `loading` would unmount and remount it on every keystroke, which
  // reads as a flicker; a refetch keeps the previous rows on screen until the
  // new ones arrive.
  const isFirstLoad = loading && providers.length === 0;

  return (
    <Box>
      <Box sx={{ display: "flex", width: "100%", my: 2 }}>
        <TextField
          placeholder={t(
            "pages.mentalHealthCareProviderManagement.filterPlaceholder",
          )}
          aria-label={t(
            "pages.mentalHealthCareProviderManagement.filterPlaceholder",
          )}
          sx={{ width: "100%" }}
          value={filterQuery}
          onChange={(e) => {
            onFilterChange(e.target.value);
          }}
        />
        <Box
          sx={{
            ml: 2,
            width: "10em",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            label={t("pages.mentalHealthCareProviderManagement.add")}
            endIcon={ButtonIcon.add}
            onClick={onAddClick}
          />
        </Box>
      </Box>

      {isFirstLoad && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Banner
          bannerType={BannerList.ERROR}
          title={t("pages.mentalHealthCareProviderManagement.loadError")}
          body={error.message}
        />
      )}

      {!isFirstLoad &&
        !error &&
        (providers.length > 0 ? (
          <Box>
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  if (page > 0) onPageChange(page - 1);
                }}
                label="&#60;"
              />
              <Box sx={{ mx: 2 }}>
                Page {page + 1} of {numPages}
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  if (page < numPages - 1) onPageChange(page + 1);
                }}
                label="&#62;"
              />
            </Box>
            <TableContainer>
              <Table
                size="small"
                aria-label={t(
                  "pages.mentalHealthCareProviderManagement.tableLabel",
                )}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("pages.mentalHealthCareProviderManagement.table.name")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t(
                        "pages.mentalHealthCareProviderManagement.table.email",
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t(
                        "pages.mentalHealthCareProviderManagement.table.trust",
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t(
                        "pages.mentalHealthCareProviderManagement.table.actions",
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providers.map((provider) => (
                    <MentalHealthCareProviderRow
                      key={provider.id}
                      provider={provider}
                      trustName={
                        provider.trustId
                          ? trustNamesById[provider.trustId]
                          : undefined
                      }
                      onEditClick={onEditClick}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <p>{t("pages.mentalHealthCareProviderManagement.empty")}</p>
        ))}
    </Box>
  );
}

export function MentalHealthCareProvidersPage() {
  const { t } = useTranslation();
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const [filterQuery, setFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const [providerToEdit, setProviderToEdit] = useState<
    MentalHealthCareProvider | undefined
  >(undefined);
  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<
    MentalHealthCareProvider | undefined
  >(undefined);

  // The input updates on every keystroke, but the search it drives is delayed:
  // the hook refetches whenever its arguments change, so an undelayed query
  // would issue a request per character typed.
  const searchQuery = useDebouncedValue(filterQuery, searchDebounceMs);

  const [{ data: providers, loading, error }, refetchProviders] =
    apiHooks.mentalHealthCareProvider.search(
      searchQuery,
      pageSize,
      page * pageSize,
    );

  const results = providers?.results ?? [];
  const numPages = providers?.count ? Math.ceil(providers.count / pageSize) : 1;

  const trustNamesById = Object.fromEntries(
    managedLocationsContext.trusts.map((trust) => [trust.id, trust.name]),
  );

  return (
    <>
      <MentalHealthCareProvidersPageInner
        providers={results}
        trustNamesById={trustNamesById}
        loading={loading}
        error={error ?? null}
        filterQuery={filterQuery}
        onFilterChange={(query) => {
          setFilterQuery(query);
          setPage(0);
        }}
        page={page}
        numPages={numPages}
        onPageChange={setPage}
        onAddClick={() => {
          setProviderToEdit(undefined);
          setShowCreateEditModal(true);
        }}
        onEditClick={(provider) => {
          setProviderToEdit(provider);
          setShowCreateEditModal(true);
        }}
        onDeleteClick={setProviderToDelete}
      />

      {showCreateEditModal && (
        <CreateEditMentalHealthCareProviderModal
          open={showCreateEditModal}
          onClose={() => {
            setShowCreateEditModal(false);
          }}
          provider={providerToEdit}
          refetchProviders={() => {
            void refetchProviders();
          }}
        />
      )}

      {providerToDelete && (
        <DeleteMentalHealthCareProviderModal
          open={true}
          onClose={() => {
            setProviderToDelete(undefined);
          }}
          provider={providerToDelete}
          onConfirm={async () => {
            try {
              const result = await api.mentalHealthCareProvider.delete(
                providerToDelete.id,
              );
              if (result.status === 204) {
                renderSuccessToast({
                  message: t(
                    "pages.mentalHealthCareProviderManagement.deleteModal.successMessage",
                    { providerName: providerToDelete.name },
                  ),
                });
              } else {
                renderErrorToast({
                  message: t(
                    "pages.mentalHealthCareProviderManagement.deleteModal.errorMessage",
                  ),
                });
              }
            } catch {
              renderErrorToast({
                message: t(
                  "pages.mentalHealthCareProviderManagement.deleteModal.errorMessage",
                ),
              });
            }
            setProviderToDelete(undefined);
            void refetchProviders();
          }}
        />
      )}
    </>
  );
}
